import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { searchContent } from '@plone/volto/actions/search/search';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import logoAgsus from '../../theme/logo-agsus.png';
import {
  comporComRecentes,
  ordenarPorAfinidade,
  semAssuntos,
} from './relacionadas';
import CabecalhoSecao from '../../components/CabecalhoSecao';
import { comporManual, consultaManual, extrairConfigs } from './compor';
import { caminhosNoHero } from './destaquesDaPagina';
import { inteiro, loteDaConsulta } from './paginacao';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const months = [
      'JAN',
      'FEV',
      'MAR',
      'ABR',
      'MAI',
      'JUN',
      'JUL',
      'AGO',
      'SET',
      'OUT',
      'NOV',
      'DEZ',
    ];
    return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return '';
  }
};

const Card = ({ item, variant, imagePosition }) => {
  const ratio = item.__agImageRatio;
  const pos = item.__agImagePosition || imagePosition || 'center';
  const getImgUrl = (it) => {
    if (it.image?.scales?.preview?.download)
      return it.image.scales.preview.download;
    if (it.image?.download) return it.image.download;
    const previewScale = it.image_scales?.preview_image_link?.[0];
    const previewDownload =
      previewScale?.scales?.larger?.download ||
      previewScale?.scales?.large?.download ||
      previewScale?.scales?.preview?.download ||
      previewScale?.download;
    if (previewScale?.base_path && previewDownload)
      return `${previewScale.base_path}/${previewDownload}`;
    if (
      it.preview_image_link &&
      typeof it.preview_image_link === 'object' &&
      it.preview_image_link['@id']
    )
      return it.preview_image_link['@id'] + '/@@images/image/larger';
    if (typeof it.preview_image_link === 'string') return it.preview_image_link;
    return null;
  };
  const rawImgUrl = getImgUrl(item);
  // A busca feita no servidor devolve URLs sobre o endereço interno da API;
  // flattenToAppURL remove tanto o prefixo público quanto o interno.
  const imgUrl = rawImgUrl ? flattenToAppURL(rawImgUrl) : null;
  const date = item.effective || item.created;
  // Destaque sem imagem vira card só de texto (fundo azul-marinho): a notícia
  // mais recente segue no destaque, sem o logo esticado num quadro de ~660 px.
  const soTexto = variant === 'hero' && !imgUrl;
  const cardClasses = `ag-card-news ag-card-news--${variant}${ratio ? ' ag-card-news--has-ratio' : ''}${soTexto ? ' ag-card-news--text' : ''}`;
  return (
    <UniversalLink href={item['@id']} className={cardClasses}>
      {!soTexto && (
        <div
          className={`ag-card-news__img${imgUrl ? '' : ' ag-card-news__img--placeholder'}`}
          style={{
            ...(imgUrl
              ? { backgroundImage: `url('${imgUrl}')` }
              : // Sem imagem de pré-visualização, a marca da Agência ocupa o
                // lugar — nunca um retângulo vazio.
                { backgroundImage: `url('${logoAgsus}')` }),
            '--ag-img-pos': pos,
            ...(ratio ? { aspectRatio: ratio } : {}),
          }}
        />
      )}
      <div className="ag-card-news__body">
        {date && (
          <div className="ag-card-news__date">
            {formatDate(date)} ·{' '}
            {(item.subjects && item.subjects[0]) || 'Notícia'}
          </div>
        )}
        <h3 className="ag-card-news__title">{item.title}</h3>
        {item.description?.trim() && (
          <p className="ag-card-news__desc">{item.description}</p>
        )}
      </div>
    </UniversalLink>
  );
};
Card.propTypes = {
  item: PropTypes.object.isRequired,
  variant: PropTypes.string,
};

const NewsGridView = ({
  data,
  block,
  excludePathname,
  emEdicao,
  properties,
}) => {
  const dispatch = useDispatch();
  const subrequestKey = `agsusNewsGrid_${block || 'preview'}`;
  const pathname = useSelector((s) => s.router?.location?.pathname || '/');

  const mode = data.mode || 'auto';
  const parentPath = data.parentPath || '/noticias';
  const limit = inteiro(data.limit, 6);
  // Na notícia aberta: o rodapé pula as relacionadas que a lateral já mostra e,
  // faltando relacionadas, completa com as mais recentes (relacionadas.js).
  const pular = inteiro(data.pular, 0, 0);
  const completar = mode === 'query' && !!data.completar;
  const chaveRecentes = `${subrequestKey}_recentes`;
  const columns = data.columns || '3';
  const layout = data.layout || 'grid';
  const portalTypes =
    data.portalTypes && data.portalTypes.length > 0
      ? data.portalTypes
      : ['News Item'];
  const sortOn = data.sortOn || 'effective';
  const imagePosition = data.imagePosition || 'center';
  // Notícia aberta: a lateral ordena por afinidade de assunto e o rodapé de
  // recentes deixa de fora as do mesmo assunto (ver relacionadas.js).
  const afinidade = data.afinidade || [];
  const excluirAssuntos = data.excluirAssuntos || [];
  // Capa (29/10/2026): a notícia do Hero da página não se repete na grade
  // automática logo abaixo; a grade completa com a seguinte.
  const noHero =
    mode === 'auto' ? caminhosNoHero(properties?.blocks, flattenToAppURL) : [];
  const noHeroChave = noHero.join('|');

  // ── selectors ─────────────────────────────────────────────
  const fetched = useSelector(
    (s) => s.search?.subrequests?.[subrequestKey]?.items || [],
    shallowEqual,
  );
  const buscaConcluida = useSelector(
    (s) => !!s.search?.subrequests?.[subrequestKey]?.loaded,
  );
  // Modo manual: itens sem UID vêm de uma busca complementar, por caminho,
  // feita só no navegador (ver consultaManual em compor.js).
  const chaveComplementar = `${subrequestKey}_caminhos`;
  const fetchedComplementar = useSelector(
    (s) => s.search?.subrequests?.[chaveComplementar]?.items || [],
    shallowEqual,
  );
  const complementarConcluida = useSelector(
    (s) => !!s.search?.subrequests?.[chaveComplementar]?.loaded,
  );
  const queryResults = useSelector(
    (s) => s.querystringsearch?.subrequests?.[subrequestKey]?.items || [],
    shallowEqual,
  );
  const consultaConcluida = useSelector(
    (s) => !!s.querystringsearch?.subrequests?.[subrequestKey]?.loaded,
  );
  const queryTotal = useSelector(
    (s) => s.querystringsearch?.subrequests?.[subrequestKey]?.total || 0,
  );
  const recentes = useSelector(
    (s) => s.search?.subrequests?.[chaveRecentes]?.items || [],
    shallowEqual,
  );

  const [currentPage, setCurrentPage] = React.useState(1);

  // ── manual mode bookkeeping ────────────────────────────────
  // Items podem ser:
  //   formato novo: [{ target: [{@id, UID, ...}], customTitle, customDescription,
  //                    imageRatio, imagePosition }]
  //   formato antigo (object_browser multiple): [{@id, title, ...}]
  // Desde 22/10/2026 (o mesmo da intranet): só o UID, o caminho e os ajustes
  // do editor são lidos daqui. Título, resumo e imagem vêm sempre da busca;
  // a cópia gravada no bloco não vira card, então notícia apagada ou
  // despublicada sai da grade sozinha (ver compor.js).
  const itemConfigs = React.useMemo(
    () => extrairConfigs(data.items, flattenToAppURL),
    [data.items],
  );
  // A consulta principal é a mesma do getAsyncData, sob a mesma chave (por
  // UID; ver consultaManual em compor.js). A complementar só existe aqui.
  const consulta = React.useMemo(
    () => consultaManual(itemConfigs),
    [itemConfigs],
  );
  const consultaKey = consulta ? JSON.stringify(consulta) : '';
  const temComplementar = !!consulta?.complementar;

  // ── query mode bookkeeping ─────────────────────────────────
  const querySig = React.useMemo(
    () => (mode === 'query' ? JSON.stringify(data.query || {}) : ''),
    [mode, data.query],
  );

  React.useEffect(() => {
    // Reset paginação se query/modo mudar
    setCurrentPage(1);
  }, [mode, querySig]);

  React.useEffect(() => {
    if (mode === 'query') {
      if (!data.query || (data.query.query || []).length === 0) return;
      dispatch(
        getQueryStringResults(
          pathname,
          {
            ...data.query,
            b_size: loteDaConsulta(data.query.b_size),
            fullobjects: true,
          },
          subrequestKey,
          currentPage,
        ),
      );
      if (completar) {
        dispatch(
          searchContent(
            parentPath,
            {
              portal_type: portalTypes,
              sort_on: 'effective',
              sort_order: 'descending',
              b_size: limit + pular + 2,
              fullobjects: true,
            },
            chaveRecentes,
          ),
        );
      }
    } else if (mode === 'manual') {
      if (!consulta) return;
      dispatch(searchContent('', consulta.principal, subrequestKey));
      if (consulta.complementar) {
        dispatch(searchContent('', consulta.complementar, chaveComplementar));
      }
    } else {
      dispatch(
        searchContent(
          parentPath,
          {
            portal_type: portalTypes,
            sort_on: sortOn,
            sort_order: 'descending',
            // com assuntos a deixar de fora, pede folga para sobrar o limite
            b_size:
              (excludePathname ? limit + 1 : limit) +
              pular +
              noHero.length +
              (excluirAssuntos.length ? 12 : 0),
            fullobjects: true,
          },
          subrequestKey,
        ),
      );
    }
  }, [
    pular,
    completar,
    chaveRecentes,
    dispatch,
    mode,
    parentPath,
    limit,
    sortOn,
    portalTypes.join(','),
    excluirAssuntos.length,
    noHeroChave,
    consultaKey,
    chaveComplementar,
    querySig,
    pathname,
    subrequestKey,
    excludePathname,
    currentPage,
  ]);

  // ── compose visible items ─────────────────────────────────
  const excludeFlat = excludePathname ? flattenToAppURL(excludePathname) : null;

  const composicao = React.useMemo(() => {
    const removeExcluded = (arr) =>
      excludeFlat
        ? arr.filter((it) => flattenToAppURL(it['@id']) !== excludeFlat)
        : arr;

    if (mode === 'manual') {
      return {
        itens: removeExcluded(
          comporManual(
            itemConfigs,
            [...fetched, ...fetchedComplementar],
            flattenToAppURL,
          ),
        ),
        completou: false,
      };
    }
    const caminho = (it) => flattenToAppURL(it['@id']);
    const excluir = excludeFlat ? [excludeFlat] : [];
    if (mode === 'query') {
      return comporComRecentes({
        itens: ordenarPorAfinidade(queryResults, afinidade),
        recentes,
        excluir,
        pular,
        limite: inteiro(data.limit, Infinity),
        completar,
        caminho,
      });
    }
    return comporComRecentes({
      itens: semAssuntos(fetched, excluirAssuntos),
      excluir: [...excluir, ...noHero],
      pular,
      limite: limit,
      caminho,
    });
  }, [
    recentes,
    pular,
    completar,
    mode,
    fetched,
    queryResults,
    fetchedComplementar,
    afinidade.join('|'),
    excluirAssuntos.join('|'),
    noHeroChave,
    limit,
    excludeFlat,
    itemConfigs,
  ]);

  const visible = composicao.itens;
  // Fora do editor, a grade manual sem nenhuma notícia disponível (apagadas,
  // despublicadas) some inteira: o aviso "Selecione notícias…" é instrução de
  // editor e não pode aparecer ao visitante, nem o título solto sobre o vazio.
  if (mode === 'manual' && visible.length === 0 && !emEdicao) return null;
  const titulo =
    composicao.completou && data.tituloCompletar
      ? data.tituloCompletar
      : data.title;

  const queryBSize = loteDaConsulta(data.query?.b_size);
  // Enquanto a busca não volta, a grade diz que está carregando; "sem
  // notícias" só depois de a busca terminar vazia (29/10/2026).
  const carregando =
    (mode === 'auto' && !buscaConcluida) ||
    (mode === 'query' &&
      (data.query?.query || []).length > 0 &&
      !consultaConcluida);
  const queryPageCount = Math.ceil(queryTotal / queryBSize);

  return (
    <section className={`ag-news-grid ag-news-grid--${layout}`}>
      <CabecalhoSecao
        prefixo="ag-news-grid"
        selo={data.eyebrow}
        titulo={titulo}
        linkTexto={data.linkText}
        linkHref={data.linkHref}
      />
      {visible.length === 0 && carregando ? (
        <p className="ag-news-grid__empty" role="status">
          Carregando as notícias…
        </p>
      ) : visible.length === 0 ? (
        <p className="ag-news-grid__empty">
          {mode === 'manual' ? (
            'Selecione notícias na barra lateral.'
          ) : mode === 'query' ? (
            'Configure a query na barra lateral.'
          ) : (
            <>
              Sem notícias publicadas em <code>{parentPath}</code> ainda.
            </>
          )}
        </p>
      ) : layout === 'featured' && visible.length >= 3 ? (
        <div className="ag-news-grid__featured">
          <Card
            item={visible[0]}
            variant="hero"
            imagePosition={imagePosition}
          />
          <div className="ag-news-grid__secondary">
            {visible.slice(1).map((item) => (
              <Card
                key={item['@id']}
                item={item}
                variant="small"
                imagePosition={imagePosition}
              />
            ))}
          </div>
        </div>
      ) : layout === 'stack' ? (
        <div className="ag-news-grid__stack">
          {visible.map((item) => (
            <Card
              key={item['@id']}
              item={item}
              variant="stack"
              imagePosition={imagePosition}
            />
          ))}
        </div>
      ) : layout === 'sidebar' ? (
        <div className="ag-news-grid__sidebar">
          {visible.map((item) => (
            <Card
              key={item['@id']}
              item={item}
              variant="sidebar"
              imagePosition={imagePosition}
            />
          ))}
        </div>
      ) : (
        <div className="ag-news-grid__items" style={{ '--ag-cols': columns }}>
          {visible.map((item) => (
            <Card
              key={item['@id']}
              item={item}
              variant="default"
              imagePosition={imagePosition}
            />
          ))}
        </div>
      )}

      {mode === 'query' && queryPageCount > 1 && layout !== 'sidebar' && (
        <nav
          className="pagination ag-news-grid__pagination"
          aria-label="Paginação"
        >
          <button
            type="button"
            className="pagination__btn pagination__btn--icon"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            aria-label="Página anterior"
          >
            ←
          </button>
          {Array.from({ length: queryPageCount }, (_, i) => i + 1)
            .filter((pg) => {
              // mostra 1, total, ±2 da atual; resto vira ellipsis
              return (
                pg === 1 ||
                pg === queryPageCount ||
                Math.abs(pg - currentPage) <= 2
              );
            })
            .reduce((acc, pg, idx, arr) => {
              if (idx > 0 && pg - arr[idx - 1] > 1) acc.push('ellipsis-' + pg);
              acc.push(pg);
              return acc;
            }, [])
            .map((pg) =>
              typeof pg === 'string' ? (
                <span key={pg} className="pagination__sep">
                  …
                </span>
              ) : (
                <button
                  key={pg}
                  type="button"
                  className={`pagination__btn${pg === currentPage ? ' pagination__btn--active' : ''}`}
                  onClick={() => setCurrentPage(pg)}
                  aria-current={pg === currentPage ? 'page' : undefined}
                >
                  {pg}
                </button>
              ),
            )}
          <span className="pagination__status">
            Página {currentPage} de {queryPageCount}
          </span>
          <button
            type="button"
            className="pagination__btn pagination__btn--icon"
            disabled={currentPage >= queryPageCount}
            onClick={() =>
              setCurrentPage((p) => Math.min(queryPageCount, p + 1))
            }
            aria-label="Próxima página"
          >
            →
          </button>
        </nav>
      )}
    </section>
  );
};

NewsGridView.propTypes = {
  emEdicao: PropTypes.bool,
  data: PropTypes.shape({
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    mode: PropTypes.string,
    parentPath: PropTypes.string,
    portalTypes: PropTypes.array,
    sortOn: PropTypes.string,
    limit: PropTypes.number,
    columns: PropTypes.string,
    layout: PropTypes.string,
    items: PropTypes.array,
    query: PropTypes.object,
  }).isRequired,
  block: PropTypes.string,
  excludePathname: PropTypes.string,
};

export default NewsGridView;
